import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api, type TestResult } from '../api';
import {
  Banner, Card, CheckList, Code, Json, StatusBadge, Tabs, relativeTime,
} from '../components/ui';

type Pane = 'checks' | 'attributes' | 'raw';

export function ResultPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const [result, setResult] = useState<TestResult | null>(null);
  const [tenant, setTenant] = useState<{ slug: string; name: string } | null>(null);
  const [error, setError] = useState('');
  const [pane, setPane] = useState<Pane>('checks');

  useEffect(() => {
    const k = params.get('k');
    api.get<{ result: TestResult; tenant: { slug: string; name: string } | null }>(
      `/api/public/results/${id}${k ? `?k=${encodeURIComponent(k)}` : ''}`,
    )
      .then((r) => { setResult(r.result); setTenant(r.tenant); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load this result'));
  }, [id, params]);

  if (error) {
    return (
      <div className="content" style={{ maxWidth: 760, margin: '40px auto' }}>
        <Banner kind="error">{error}</Banner>
        <p className="faint small">
          Result links expire. If you are an administrator for this tenant, open the result
          from the tenant’s Results tab instead.
        </p>
        <Link to="/">Back to the console</Link>
      </div>
    );
  }
  if (!result) return <div className="content"><span className="faint">Loading…</span></div>;

  const checks = result.summary.checks ?? [];
  const failed = checks.filter((c) => c.status === 'fail');
  const attributes = result.summary.attributes ?? {};
  const untrusted = result.summary.trusted === false;

  return (
    <div className="content" style={{ maxWidth: 1000, margin: '0 auto', paddingTop: 28 }}>
      <div className="row" style={{ marginBottom: 6 }}>
        <span className="badge proto">{result.protocol.toUpperCase()}</span>
        <StatusBadge status={result.status} />
        <div className="spacer" />
        <span className="faint small">{relativeTime(result.createdAt)}</span>
      </div>

      <h1 className="page-title" style={{ marginBottom: 2 }}>
        {result.subject || 'No subject returned'}
      </h1>
      <p className="page-sub" style={{ marginBottom: 20 }}>
        {result.connectionName}
        {tenant && <> · {tenant.name}</>}
      </p>

      {untrusted && (
        <Banner kind="error">
          <strong>Signature verification failed.</strong> Everything below was read out of a
          document whose signature could not be verified. Treat the subject and attributes as
          attacker-controlled, not as facts about a user.
        </Banner>
      )}

      {failed.length > 0 && !untrusted && (
        <Banner kind="error">
          {failed.length} check{failed.length > 1 ? 's' : ''} failed. Start with{' '}
          <strong>{failed[0].label}</strong>
          {failed[0].detail ? <> — {failed[0].detail}</> : null}
        </Banner>
      )}
      {failed.length === 0 && result.status === 'success' && (
        <Banner kind="success">
          Every check passed. This connection is working end to end.
        </Banner>
      )}

      <Tabs<Pane>
        active={pane} onChange={setPane}
        tabs={[
          { id: 'checks', label: 'Checks', count: checks.length },
          { id: 'attributes', label: 'Claims & attributes', count: Object.keys(attributes).length },
          { id: 'raw', label: 'Raw protocol data' },
        ]}
      />

      {pane === 'checks' && (
        <Card>
          <CheckList checks={checks} sorted />
        </Card>
      )}

      {pane === 'attributes' && (
        <Card
          title="What the identity provider actually sent"
          hint="These are the attributes an application would receive for this user."
        >
          {Object.keys(attributes).length === 0 ? (
            <p className="faint">No attributes or claims were returned.</p>
          ) : (
            <dl className="kv">
              {Object.entries(attributes).map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt>{k}</dt>
                  <dd>{formatValue(v)}</dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      )}

      {pane === 'raw' && <RawPanes result={result} />}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function RawPanes({ result }: { result: TestResult }) {
  const d = result.detail ?? {};

  if (result.protocol === 'saml') {
    return (
      <>
        {typeof d.rawXml === 'string' && (
          <Card title="SAMLResponse" hint="Exactly what was POSTed to the ACS, pretty-printed.">
            <Code>{d.rawXml}</Code>
          </Card>
        )}
        {typeof d.authnRequestXml === 'string' && d.authnRequestXml && (
          <Card title="AuthnRequest we sent">
            <Code>{d.authnRequestXml}</Code>
          </Card>
        )}
        {Array.isArray(d.signatures) && d.signatures.length > 0 && (
          <Card title="Signatures found in the document">
            <Json value={d.signatures} />
          </Card>
        )}
        {typeof d.samlResponseBase64 === 'string' && (
          <Card title="Base64 SAMLResponse" hint="Paste into any external SAML decoder to cross-check.">
            <Code>{d.samlResponseBase64}</Code>
          </Card>
        )}
      </>
    );
  }

  if (result.protocol === 'oidc') {
    return (
      <>
        {d.decodedIdToken && (
          <Card title="id_token" hint="Header and payload, decoded. See the Checks tab for the verification verdict.">
            <Json value={d.decodedIdToken} />
          </Card>
        )}
        {d.decodedAccessToken && (
          <Card title="access_token">
            <Json value={d.decodedAccessToken} />
          </Card>
        )}
        {d.userinfo != null && (
          <Card title="UserInfo response"><Json value={d.userinfo} /></Card>
        )}
        {d.tokenResponse != null && (
          <Card title="Token endpoint exchange" hint="Raw request and response from the token endpoint.">
            <Json value={{ request: d.tokenRequest, response: d.tokenResponse }} />
          </Card>
        )}
        {d.authorizationRequest != null && (
          <Card title="Authorization request we sent"><Json value={d.authorizationRequest} /></Card>
        )}
        {d.resolvedEndpoints != null && (
          <Card title="Resolved provider endpoints"><Json value={d.resolvedEndpoints} /></Card>
        )}
      </>
    );
  }

  // PingAccess
  return (
    <>
      {Array.isArray(d.headers) && (
        <Card
          title="Request headers as they arrived"
          hint="Injected identity headers are highlighted; forwarding headers show the proxy chain."
        >
          <table>
            <thead><tr><th>Header</th><th>Value</th><th>Kind</th></tr></thead>
            <tbody>
              {(d.headers as { name: string; value: string; category: string }[]).map((h) => (
                <tr key={h.name}>
                  <td className="mono nowrap">{h.name}</td>
                  <td className="mono" style={{ wordBreak: 'break-all', maxWidth: 520 }}>{h.value}</td>
                  <td>
                    <span className={`badge ${h.category === 'identity' || h.category === 'token' ? 'pass' : 'proto'}`}>
                      {h.category}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {d.token != null && (
        <Card title="Mediated token"><Json value={d.token} /></Card>
      )}
      <Card title="Request">
        <Json value={{ method: d.method, path: d.path, protocol: d.protocol, remoteAddress: d.remoteAddress }} />
      </Card>
    </>
  );
}
